/**
 * UW Toolkit — Frontend Calculation Mirror
 * ==========================================
 * Must match backend/routes/uw_calculations.py EXACTLY.
 *
 * Rounding Policy:
 *   - All currency values: round to 2 decimal places
 *   - Payment amount rounded FIRST, then used for derived estimates
 *   - APR: round to 2 decimal places
 *
 * Payment Count (from term in business days):
 *   - Daily:     term
 *   - Weekly:    ceil(term / 5)
 *   - Bi-Weekly: ceil(term / 10)
 *   - Monthly:   ceil(term / 20)
 *
 * APR = ((payback / net_funding) - 1) * (365 / calendar_days) * 100
 *
 * Validation Bounds (must match backend):
 *   - advance:  (0, 100_000_000]
 *   - factor:   [1.0, 10.0]
 *   - term:     [1, 3650]
 *   - fees:     [0, advance)
 *   - payment_count_override: [1, 10_000]
 */

const ALLOWED_FREQUENCIES = ['Daily', 'Weekly', 'Bi-Weekly', 'Monthly'];

const MAX_ADVANCE = 100_000_000;
const MAX_FACTOR = 10.0;
const MAX_TERM = 3650;
const MAX_PAYMENT_COUNT = 10_000;

function r2(v) {
  return Math.round(v * 100) / 100;
}

function isFiniteNum(v) {
  const n = Number(v);
  return Number.isFinite(n);
}

/**
 * Validate intake inputs. Returns array of error strings (empty = valid).
 * Must match backend validate_intake_inputs() exactly.
 */
export function validateIntake({
  advance, factor, term, paymentFrequency,
  originationFee = 0, brokerFee = 0, otherFees = 0,
  netFundingOverride = null, paymentCountOverride = null,
} = {}) {
  const errors = [];

  // advance
  if (!isFiniteNum(advance) || advance === '' || advance === null) {
    errors.push('Advance must be a valid number');
  } else {
    const adv = Number(advance);
    if (adv <= 0) errors.push('Advance must be greater than 0');
    else if (adv > MAX_ADVANCE) errors.push(`Advance cannot exceed $${MAX_ADVANCE.toLocaleString()}`);
  }

  // factor
  if (!isFiniteNum(factor) || factor === '' || factor === null) {
    errors.push('Factor rate must be a valid number');
  } else {
    const fac = Number(factor);
    if (fac < 1.0) errors.push('Factor rate must be >= 1.00');
    else if (fac > MAX_FACTOR) errors.push(`Factor rate cannot exceed ${MAX_FACTOR}`);
  }

  // term
  if (!isFiniteNum(term) || term === '' || term === null) {
    errors.push('Term must be a valid integer');
  } else {
    const t = Math.floor(Number(term));
    if (t <= 0) errors.push('Term must be greater than 0');
    else if (t > MAX_TERM) errors.push(`Term cannot exceed ${MAX_TERM} business days`);
  }

  // frequency
  if (!paymentFrequency || !ALLOWED_FREQUENCIES.includes(paymentFrequency)) {
    errors.push(`Payment frequency must be one of: ${ALLOWED_FREQUENCIES.join(', ')}`);
  }

  // fees
  for (const [label, val] of [['Origination fee', originationFee], ['Broker fee', brokerFee], ['Other fees', otherFees]]) {
    if (val !== '' && val !== null && val !== undefined) {
      if (!isFiniteNum(val)) errors.push(`${label} must be a valid number`);
      else if (Number(val) < 0) errors.push(`${label} must be >= 0`);
    }
  }

  // overrides
  if (netFundingOverride !== null && netFundingOverride !== '' && netFundingOverride !== undefined) {
    if (!isFiniteNum(netFundingOverride)) errors.push('Net funding override must be a valid number');
    else if (Number(netFundingOverride) < 0) errors.push('Net funding override must be >= 0');
  }

  if (paymentCountOverride !== null && paymentCountOverride !== '' && paymentCountOverride !== undefined) {
    if (!isFiniteNum(paymentCountOverride)) errors.push('Payment count override must be a valid integer');
    else {
      const pco = Math.floor(Number(paymentCountOverride));
      if (pco < 1) errors.push('Payment count override must be >= 1');
      else if (pco > MAX_PAYMENT_COUNT) errors.push(`Payment count override cannot exceed ${MAX_PAYMENT_COUNT}`);
    }
  }

  // cross-field: total fees < advance
  if (errors.length === 0) {
    const adv = Number(advance);
    const totalFees = (Number(originationFee) || 0) + (Number(brokerFee) || 0) + (Number(otherFees) || 0);
    const nfo = netFundingOverride ? Number(netFundingOverride) : 0;
    if (totalFees >= adv && nfo <= 0) {
      errors.push('Total fees must be less than advance amount (or provide a net funding override)');
    }
  }

  return errors;
}


function derivePaymentCount(term, frequency) {
  if (frequency === 'Daily') return term;
  if (frequency === 'Weekly') return Math.ceil(term / 5);
  if (frequency === 'Bi-Weekly') return Math.ceil(term / 10);
  if (frequency === 'Monthly') return Math.ceil(term / 20);
  return term;
}

function estimateCalendarDays(paymentCount, frequency) {
  if (frequency === 'Daily') return Math.ceil(paymentCount * 7 / 5);
  if (frequency === 'Weekly') return paymentCount * 7;
  if (frequency === 'Bi-Weekly') return paymentCount * 14;
  if (frequency === 'Monthly') return Math.round(paymentCount * 30.44);
  return paymentCount;
}

export function calculateIntake({
  advance = 0,
  factor = 1,
  term = 0,
  paymentFrequency = 'Daily',
  originationFee = 0,
  brokerFee = 0,
  otherFees = 0,
  netFundingOverride = null,
  paymentCountOverride = null,
  fundingDate = null,
} = {}) {
  const adv = isFiniteNum(advance) ? Number(advance) : 0;
  const fac = isFiniteNum(factor) ? Number(factor) : 1;
  const trm = isFiniteNum(term) ? Math.max(0, Math.floor(Number(term))) : 0;
  const origFee = isFiniteNum(originationFee) ? Number(originationFee) : 0;
  const brkFee = isFiniteNum(brokerFee) ? Number(brokerFee) : 0;
  const othFee = isFiniteNum(otherFees) ? Number(otherFees) : 0;

  if (adv <= 0 || fac < 1 || trm <= 0) {
    return null; // Invalid inputs — no calculation
  }

  // Core
  const payback = r2(adv * fac);
  const financeCharge = r2(payback - adv);

  // Payment count (guard divide-by-zero)
  const pcoRaw = paymentCountOverride && isFiniteNum(paymentCountOverride) ? Math.floor(Number(paymentCountOverride)) : 0;
  const paymentCount = pcoRaw > 0 ? pcoRaw : derivePaymentCount(trm, paymentFrequency);

  // Payment amount (rounded, then reused; guard divide-by-zero)
  const paymentAmount = paymentCount > 0 ? r2(payback / paymentCount) : 0;

  // Net funding
  const totalFees = r2(origFee + brkFee + othFee);
  const nfo = netFundingOverride && isFiniteNum(netFundingOverride) ? Number(netFundingOverride) : 0;
  const netFunding = nfo > 0 ? r2(nfo) : r2(adv - totalFees);

  // Monthly cost (uses rounded paymentAmount)
  let monthlyCost = 0;
  if (paymentFrequency === 'Daily') monthlyCost = r2(paymentAmount * 20);
  else if (paymentFrequency === 'Weekly') monthlyCost = r2(paymentAmount * (52 / 12));
  else if (paymentFrequency === 'Bi-Weekly') monthlyCost = r2(paymentAmount * (26 / 12));
  else monthlyCost = r2(paymentAmount);

  // Weekly cost
  let weeklyCost = 0;
  if (paymentFrequency === 'Daily') weeklyCost = r2(paymentAmount * 5);
  else if (paymentFrequency === 'Weekly') weeklyCost = r2(paymentAmount);
  else if (paymentFrequency === 'Bi-Weekly') weeklyCost = paymentAmount > 0 ? r2(paymentAmount / 2) : 0;
  else weeklyCost = r2(paymentAmount * 12 / 52);

  // APR (guard divide-by-zero)
  const calendarDays = paymentCount > 0 ? estimateCalendarDays(paymentCount, paymentFrequency) : 0;
  let apr = 0;
  if (netFunding > 0 && calendarDays > 0) {
    apr = r2(((payback / netFunding - 1) * (365 / calendarDays)) * 100);
  }

  // Maturity date
  let maturityDate = null;
  if (fundingDate && calendarDays > 0) {
    try {
      const fd = new Date(fundingDate);
      if (!isNaN(fd.getTime())) {
        const md = new Date(fd.getTime() + calendarDays * 86400000);
        maturityDate = `${String(md.getMonth() + 1).padStart(2, '0')}/${String(md.getDate()).padStart(2, '0')}/${md.getFullYear()}`;
      }
    } catch { /* ignore */ }
  }

  return {
    advance: adv,
    factor: fac,
    term: trm,
    paymentFrequency,
    originationFee: origFee,
    brokerFee: brkFee,
    otherFees: othFee,
    totalFees,
    fundingDate,
    paymentCount,
    calendarDays,
    payback,
    financeCharge,
    paymentAmount,
    netFunding,
    monthlyCostEst: monthlyCost,
    weeklyCostEst: weeklyCost,
    aprEstimate: apr,
    maturityDateEst: maturityDate,
  };
}

export { ALLOWED_FREQUENCIES };
